import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, ConfirmDialog, Field, FormActions, formatMoney, inputClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { download, failed, lastMonth, monthName, tdClass, thClass } from './hrShared';

interface PayLine {
  code: string;
  name: string;
  amount: number;
  basis?: string;
}

interface Line {
  id: string;
  employeeId: string;
  code: string;
  name: string;
  department: string | null;
  wageBasis: string;
  paidDays: number;
  workedDays: number;
  overtimeHours: number;
  earnings: PayLine[];
  deductions: PayLine[];
  employer: PayLine[];
  otherAddition: number;
  otherDeduction: number;
  adjustmentNote: string | null;
  gross: number;
  totalDeductions: number;
  net: number;
}

interface Run {
  id: string;
  period: string;
  status: 'draft' | 'final';
  totalGross: string;
  totalDeductions: string;
  totalNet: string;
  totalEmployer: string;
  warnings: string[];
  employees?: number;
  lines?: Line[];
}

const LineDetail: React.FC<{ run: Run; line: Line; canEdit: boolean; onSaved: (run: Run) => void }> = ({ run, line, canEdit, onSaved }) => {
  const [addition, setAddition] = useState(String(line.otherAddition || ''));
  const [deduction, setDeduction] = useState(String(line.otherDeduction || ''));
  const [note, setNote] = useState(line.adjustmentNote ?? '');
  const save = useMutation({
    mutationFn: async () => (await api.put(`/hr/payroll/${run.id}/lines/${line.id}`, { otherAddition: Number(addition || 0), otherDeduction: Number(deduction || 0), adjustmentNote: note.trim() || null })).data as Run,
    onSuccess: (result) => {
      toast.success('Saved');
      onSaved(result);
    },
    onError: failed('Could not save'),
  });
  const table = (title: string, items: PayLine[]) => (
    <table className="min-w-full divide-y divide-gray-200 rounded-md ring-1 ring-gray-200">
      <thead className="bg-gray-50"><tr><th className={thClass}>{title}</th><th className={cn(thClass, 'text-right')}>₹</th></tr></thead>
      <tbody className="divide-y divide-gray-100">
        {items.map((item) => (
          <tr key={item.code}><td className={tdClass}>{item.name}{item.basis ? <span className="block text-xs text-gray-500">{item.basis}</span> : null}</td><td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(item.amount)}</td></tr>
        ))}
        {items.length === 0 && <tr><td className={tdClass} colSpan={2}>None</td></tr>}
      </tbody>
    </table>
  );
  return (
    <div className="space-y-4" data-testid="payroll-line">
      <p className="text-sm text-gray-700">Days paid {line.paidDays} (worked {line.workedDays}) · overtime {line.overtimeHours} h · {line.wageBasis === 'daily' ? 'daily wage' : 'monthly pay'}</p>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {table('Earnings', line.earnings)}
        {table('Deductions', line.deductions)}
      </div>
      {line.employer.length > 0 && table('Paid by the employer on top', line.employer)}
      <p className="text-base font-semibold text-gray-900">Gross {formatMoney(line.gross)} · deductions {formatMoney(line.totalDeductions)} · net pay {formatMoney(line.net)}</p>
      {run.status === 'draft' && canEdit && (
        <form className="grid grid-cols-1 gap-3 rounded-md bg-gray-50 p-3 sm:grid-cols-4" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
          <Field label="Add (₹)" htmlFor="pl-add" hint="A bonus, an arrear"><input id="pl-add" type="number" min={0} step="0.01" value={addition} onChange={(event) => setAddition(event.target.value)} className={inputClass} /></Field>
          <Field label="Take off (₹)" htmlFor="pl-deduct" hint="An advance, a loan"><input id="pl-deduct" type="number" min={0} step="0.01" value={deduction} onChange={(event) => setDeduction(event.target.value)} className={inputClass} /></Field>
          <Field label="Note" htmlFor="pl-note"><input id="pl-note" maxLength={200} value={note} onChange={(event) => setNote(event.target.value)} className={inputClass} /></Field>
          <div className="flex items-end"><button type="submit" disabled={save.isPending} className={primaryButtonClass}>{save.isPending ? 'Saving…' : 'Save'}</button></div>
        </form>
      )}
      <FormActions>
        <button type="button" onClick={() => download(`/hr/payroll/${run.id}/payslips/${line.id}`, `Payslip_${run.period}_${line.code}.pdf`)} className={secondaryButtonClass}>
          <ArrowDownTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />
          Payslip PDF
        </button>
      </FormActions>
    </div>
  );
};

const PayrollPanel: React.FC<{ canEdit: boolean; canFinalise: boolean; canExport: boolean }> = ({ canEdit, canFinalise, canExport }) => {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(lastMonth());
  const [openId, setOpenId] = useState<string | null>(null);
  const [lineId, setLineId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'finalise' | 'cancel' | null>(null);
  const { data: runs = [] } = useQuery<Run[]>({ queryKey: ['hr', 'payroll'], queryFn: async () => (await api.get('/hr/payroll')).data });
  const { data: run } = useQuery<Run>({ queryKey: ['hr', 'payroll', openId], queryFn: async () => (await api.get(`/hr/payroll/${openId}`)).data, enabled: !!openId });
  const keep = (result: Run) => {
    queryClient.setQueryData(['hr', 'payroll', result.id], result);
    queryClient.invalidateQueries({ queryKey: ['hr', 'payroll'], exact: true });
  };
  const start = useMutation({
    mutationFn: async () => (await api.post('/hr/payroll', { period: month })).data as Run,
    onSuccess: (result) => {
      toast.success(`Payroll of ${monthName(result.period)} worked out as a draft`);
      keep(result);
      setOpenId(result.id);
    },
    onError: failed('Could not work out the payroll'),
  });
  const recompute = useMutation({ mutationFn: async () => (await api.post(`/hr/payroll/${openId}/recompute`)).data as Run, onSuccess: (result) => { toast.success('Worked out again'); keep(result); }, onError: failed('Could not work it out again') });
  const finalise = useMutation({ mutationFn: async () => (await api.post(`/hr/payroll/${openId}/finalise`)).data as Run, onSuccess: (result) => { toast.success('Payroll is final'); keep(result); setConfirm(null); }, onError: failed('Could not make it final') });
  const cancel = useMutation({ mutationFn: async () => api.post(`/hr/payroll/${openId}/cancel`), onSuccess: () => { toast.success('Draft thrown away'); setOpenId(null); setConfirm(null); queryClient.invalidateQueries({ queryKey: ['hr', 'payroll'] }); }, onError: failed('Could not throw it away') });
  const line = run?.lines?.find((item) => item.id === lineId) ?? null;

  if (openId && run) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <button type="button" onClick={() => setOpenId(null)} className="text-sm font-medium text-indigo-600">← All payrolls</button>
            <h3 className="text-lg font-semibold text-gray-900">Payroll of {monthName(run.period)} <span className={cn(badgeClass, 'ml-2', run.status === 'final' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700')}>{run.status === 'final' ? 'Final' : 'Draft'}</span></h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {canExport && <button type="button" onClick={() => download(`/hr/payroll/${run.id}/export`, `Payroll_${run.period}.csv`)} className={secondaryButtonClass}><ArrowDownTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />Register</button>}
            {run.status === 'draft' && canEdit && <button type="button" onClick={() => recompute.mutate()} disabled={recompute.isPending} className={secondaryButtonClass}>{recompute.isPending ? 'Working…' : 'Work Out Again'}</button>}
            {run.status === 'draft' && canEdit && <button type="button" onClick={() => setConfirm('cancel')} className={secondaryButtonClass}>Throw Away</button>}
            {run.status === 'draft' && canFinalise && <button type="button" onClick={() => setConfirm('finalise')} className={primaryButtonClass}>Make Final</button>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="payroll-totals">
          {[['Gross', run.totalGross], ['Deductions', run.totalDeductions], ['Net pay', run.totalNet], ['Employer on top', run.totalEmployer]].map(([label, value]) => (
            <div key={label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p><p className="mt-1 text-xl font-semibold tabular-nums text-gray-900">{formatMoney(Number(value))}</p></div>
          ))}
        </div>
        {run.warnings.length > 0 && <ul className="space-y-1 rounded-md bg-amber-50 p-3 text-sm text-amber-800" data-testid="payroll-warnings">{run.warnings.map((warning) => (<li key={warning}>{warning}</li>))}</ul>}
        <div className="overflow-x-auto rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr><th className={thClass}>Employee</th><th className={cn(thClass, 'text-right')}>Days paid</th><th className={cn(thClass, 'text-right')}>OT h</th><th className={cn(thClass, 'text-right')}>Gross</th><th className={cn(thClass, 'text-right')}>Deductions</th><th className={cn(thClass, 'text-right')}>Net pay</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {(run.lines ?? []).map((item) => (
                <tr key={item.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setLineId(item.id)} data-testid="payroll-row">
                  <td className={tdClass}><span className="font-mono text-xs text-gray-500">{item.code}</span> {item.name}{item.adjustmentNote ? <span className="block text-xs text-gray-500">{item.adjustmentNote}</span> : null}</td>
                  <td className={cn(tdClass, 'text-right tabular-nums')}>{item.paidDays}</td>
                  <td className={cn(tdClass, 'text-right tabular-nums')}>{item.overtimeHours}</td>
                  <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(item.gross)}</td>
                  <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(item.totalDeductions)}</td>
                  <td className={cn(tdClass, 'text-right font-medium tabular-nums')}>{formatMoney(item.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Modal isOpen={!!line} onClose={() => setLineId(null)} title={line ? `${line.code} ${line.name}` : ''} size="2xl">
          {line && <LineDetail key={line.id + line.net} run={run} line={line} canEdit={canEdit} onSaved={keep} />}
        </Modal>
        <ConfirmDialog
          isOpen={confirm !== null}
          onClose={() => setConfirm(null)}
          onConfirm={() => (confirm === 'finalise' ? finalise.mutate() : cancel.mutate())}
          title={confirm === 'finalise' ? `Make the payroll of ${monthName(run.period)} final` : 'Throw away this draft'}
          message={confirm === 'finalise' ? 'The payroll can no longer change, nor can the attendance of these employees in this month.' : 'The draft is removed. The attendance stays as it is.'}
          confirmLabel={confirm === 'finalise' ? 'Make Final' : 'Throw Away'}
          pendingLabel="Working…"
          isPending={finalise.isPending || cancel.isPending}
          tone={confirm === 'finalise' ? 'primary' : 'danger'}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
          <label className="text-sm text-gray-700">Month<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} aria-label="Payroll month" className={cn(inputClass, 'mt-1 w-44')} /></label>
          <button type="button" onClick={() => start.mutate()} disabled={start.isPending || !month} className={primaryButtonClass}>{start.isPending ? 'Working…' : 'Work Out Payroll'}</button>
          <p className="text-xs text-gray-500">For the employees on the rolls, from the attendance of the month and the pay components. Contract workers are paid by their contractor.</p>
        </div>
      )}
      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr><th className={thClass}>Month</th><th className={thClass}>Status</th><th className={cn(thClass, 'text-right')}>Employees</th><th className={cn(thClass, 'text-right')}>Gross</th><th className={cn(thClass, 'text-right')}>Net pay</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {runs.map((item) => (
              <tr key={item.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setOpenId(item.id)} data-testid="payroll-run">
                <td className={cn(tdClass, 'font-medium text-indigo-700')}>{monthName(item.period)}</td>
                <td className={tdClass}><span className={cn(badgeClass, item.status === 'final' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700')}>{item.status === 'final' ? 'Final' : 'Draft'}</span></td>
                <td className={cn(tdClass, 'text-right tabular-nums')}>{item.employees}</td>
                <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(Number(item.totalGross))}</td>
                <td className={cn(tdClass, 'text-right tabular-nums')}>{formatMoney(Number(item.totalNet))}</td>
              </tr>
            ))}
            {runs.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-gray-500">No payroll worked out yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PayrollPanel;
